import { FC, useMemo, useEffect, useRef } from 'react';
import { Draft } from 'src/types/common';
import { IAIRequest } from 'src/types/org/ai/schoolInterface';
import { IFieldErrors } from 'src/types/validation';

// Import panel components
import {
  OrganizationInformationPanel,
  AddressPanel,
  OrganizationDetailsPanel,
  ExamsInformationPanel,
  TestPrepPanel,
  ExamSecurityPanel,
  SubmitterCommentsPanel,
  OnsiteInstructionPanel,
  AccreditationPanel
} from 'src/containers/school/ai/components';

export interface IAIFieldProps {
  categoryId: string;
  userId: string;
  readOnly?: boolean;
  item: Draft<IAIRequest>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IAIRequest>;
  showDiff?: boolean;
  onUpdate?: (item: Draft<IAIRequest>) => void;
}

// Helper: Create a cleared Level 2 data payload
const getLevel2ClearedData = (initial: Draft<IAIRequest>): Draft<IAIRequest> => ({
  ...initial,
  appliedLevel: '1',
  data: {
    ...initial.data,
    ai: {
      ...initial.data?.ai,
      appliedLevel: '1'
    },
    appForm: {
      ...initial.data?.appForm,
      // Clear ExamsInformation Level 2 fields INCLUDING the trigger fields
      examsInformation: {
        ...initial.data?.appForm?.examsInformation,
        interestedInPSATSuite: false,
        interestedInSAT: false,
        satAdministrationTypes: [], // This clears Weekend selection
        earliestAdministrationDate: undefined
      },
      // Clear OnsiteInstructionPanel fields
      studentSchedule: {
        ...initial.data?.appForm?.studentSchedule,
        instructionDays: [],
        daySchedule: {},
        hoursOnsite: {}
      },
      // Clear TestPrepPanel fields
      aiTestPrep: {
        ...initial.data?.appForm?.aiTestPrep,
        hasTestPrep: undefined,
        description: undefined,
        schedule: undefined,
        onlyForEnrolledStudents: undefined,
        hasStudentFees: undefined,
        isOutsideCompany: undefined,
        hasInstructorCompensation: undefined,
        hasAccessToSecureMaterials: undefined
      },
      // Clear ExamSecurityPanel fields
      examSecurity: {
        ...initial.data?.appForm?.examSecurity,
        heldAtVisitingAddress: undefined,
        alternativeTestingAddress: undefined,
        alternativeAddressExplanation: undefined,
        materialReceivedBy: undefined,
        materialReceivingArea: [],
        materialReceivingAreaOther: undefined,
        materialStorageLocation: undefined,
        isMaterialStorageLocked: undefined,
        securityResponsible: undefined,
        allowNonEmployedAccess: undefined,
        hasDesignatedTestingRooms: undefined,
        hasTestingRoomPowerWifi: undefined
      }
    }
  }
});

export const AIFields: FC<IAIFieldProps> = ({
  item: initial,
  readOnly,
  errors,
  onUpdate
}) => {
  const changed = (updates: Draft<IAIRequest>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  // Track authorization level on mount
  const initialLevelRef = useRef<'1' | '2' | null>(null);
  if (initialLevelRef.current === null) {
    initialLevelRef.current = (initial.data?.ai?.appliedLevel || initial.appliedLevel || '1') as '1' | '2';
  }
  const initialLevel = initialLevelRef.current;

  // Track manual vs auto level changes
  const manualLevelChangeRef = useRef<boolean>(false);
  const isAutoToggleRef = useRef<boolean>(false);
  const prevLevelRef = useRef<'1' | '2'>(
    (initial.data?.ai?.appliedLevel || initial.appliedLevel || '1') as '1' | '2'
  );

  // Detect manual level changes (ignoring auto-toggles)
  useEffect(() => {
    const currentLevel = (initial.data?.ai?.appliedLevel || initial.appliedLevel || '1') as '1' | '2';
    
    if (prevLevelRef.current !== null && prevLevelRef.current !== currentLevel) {
      if (!isAutoToggleRef.current) {
        // This was a manual change
        manualLevelChangeRef.current = true;
      }else {
        // This was an auto-toggle, reset the flag NOW
        isAutoToggleRef.current = false;
      }
    }
  }, [initial.data?.ai?.appliedLevel, initial.appliedLevel]);

  // Use useMemo to reactively determine if Level 2 panels should be shown
  const shouldShowLevel2Panels = useMemo(() => {
    // Get the current applied level
    const currentLevel = initial.data?.ai?.appliedLevel || initial.appliedLevel || '1';

    // Always show Level 2 panels when current level is '2'
    // This handles all cases: started with 1 or 2, manual changes, auto-toggles
    return currentLevel === '2';
  }, [
    initial.data?.ai?.appliedLevel,
    initial.appliedLevel
  ]);

    // Auto-toggle authorization level based on PSAT/Weekend selections
  useEffect(() => {
  if (readOnly) return;

  const currentLevel = (initial.data?.ai?.appliedLevel || initial.appliedLevel || '1') as '1' | '2';
  const previousLevel = prevLevelRef.current;
  const examsInformation = initial.data?.appForm?.examsInformation;
  const interestedInPSATSuite = examsInformation?.interestedInPSATSuite;
  const satAdministrationTypes = examsInformation?.satAdministrationTypes || [];
  const hasWeekendSAT = satAdministrationTypes.includes('Weekend');

  // CASE 1: User manually downgraded to Level 1 from Level 2
  // This handles: started L1 → auto-upgraded to L2 → manually downgraded to L1
  if (
    currentLevel === '1' &&
    prevLevelRef.current === '2' &&
    manualLevelChangeRef.current
  ) {
    isAutoToggleRef.current = true;
    manualLevelChangeRef.current = false;
    changed(getLevel2ClearedData(initial));
    isAutoToggleRef.current = false;
    prevLevelRef.current = currentLevel;
    return;
  }

  // CASE 2: Auto-upgrade to Level 2 (started with Level 1, PSAT or Weekend selected)
  if (
    currentLevel === '1' &&
    initialLevel === '1' &&
    !manualLevelChangeRef.current &&
    (interestedInPSATSuite === true || hasWeekendSAT)
  ) {
    isAutoToggleRef.current = true;
    changed({
      ...initial,
      appliedLevel: '2',
      data: {
        ...initial.data,
        ai: {
          ...initial.data?.ai,
          appliedLevel: '2'
        }
      }
    });
    prevLevelRef.current = currentLevel;
    return;
  }

  // CASE 3: Auto-downgrade to Level 1 (no manual change, no triggers present)
  // Only applies if started at Level 1 and no manual changes occurred
  if (
    currentLevel === '2' &&
    initialLevel === '1' &&
    !manualLevelChangeRef.current &&
    interestedInPSATSuite !== true &&
    !hasWeekendSAT
  ) {
    isAutoToggleRef.current = true;
    changed(getLevel2ClearedData(initial));
    isAutoToggleRef.current = false;
    prevLevelRef.current = currentLevel;
    return;
  }
  // Reset flag if no cases triggered
  isAutoToggleRef.current = false;
  prevLevelRef.current = currentLevel;
}, [
  initial.data?.appForm?.examsInformation?.interestedInPSATSuite,
  initial.data?.appForm?.examsInformation?.satAdministrationTypes,
  initial.data?.ai?.appliedLevel,
  initial.appliedLevel,
  initialLevel,
  readOnly
]);

  const currentLevel = (initial.data?.ai?.appliedLevel || initial.appliedLevel || '1') as '1' | '2';

  return (
    <>
      <OrganizationInformationPanel
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />

      <AddressPanel
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />

      <OrganizationDetailsPanel
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />

      <ExamsInformationPanel
        key={`exams-${currentLevel}-${(initial.data?.appForm?.examsInformation?.satAdministrationTypes || []).join(',')}-${initial.data?.appForm?.examsInformation?.interestedInPSATSuite}`}
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />

      <AccreditationPanel
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />

      {/* Show Level 2 panels when: appliedLevel is 2 OR interestedInPSATSuite is Yes OR interestedInSAT is Yes */}
      {shouldShowLevel2Panels && (
        <>
          <OnsiteInstructionPanel
            item={initial}
            readOnly={readOnly}
            errors={errors}
            onUpdate={changed}
          />

          <TestPrepPanel
            item={initial}
            readOnly={readOnly}
            errors={errors}
            onUpdate={changed}
          />

          <ExamSecurityPanel
            item={initial}
            readOnly={readOnly}
            errors={errors}
            onUpdate={changed}
          />
        </>
      )}

      <SubmitterCommentsPanel
        item={initial}
        readOnly={readOnly}
        errors={errors}
        onUpdate={changed}
      />
    </>
  );
};